/**
 * Form values and rules of the event dialog and the list helpers of the events
 * screen.
 */
import { DMS_EVENT_TYPE } from '@/shared/constants';
import { getImage } from '@/shared/lib/image';
import { sortByKey } from '@/shared/lib/sort';
import { textKey, type ListSort } from '../list';
import type { DmsEvent, EventInput } from './api';

export const DEFAULT_EVENT_PICTURE =
  '/dms-front-assets/configurator/events/dms-thumbnail-2025.webp';

/** 5 MB files go through while the message still says 500 kB (see migration.md). */
export const EVENT_PICTURE_MAX_KB = 5000;

export interface EventFormValues {
  name: string;
  location: string;
  eventId: string;
  /** `YYYY-MM-DD`, the native date input value. */
  startDate: string;
  endDate: string;
  storeCode: string;
}

export type EventFormErrors = Partial<Record<keyof EventFormValues, string>>;

const pad = (n: number) => String(n).padStart(2, '0');

/** Local calendar day of an ISO date, as the calendar picker shows it. */
export const toDateInputValue = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** The picked day at local midnight. */
export const dateInputToIso = (value: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  ).toISOString();
};

export const eventFormValues = (item: DmsEvent | null): EventFormValues => ({
  name: item?.name ?? '',
  location: item?.location ?? '',
  eventId: item?.eventId ?? '',
  startDate: toDateInputValue(item?.startDate),
  endDate: toDateInputValue(item?.endDate),
  storeCode: item?.storeCode ?? '',
});

export const validateEventForm = (values: EventFormValues): EventFormErrors => {
  const errors: EventFormErrors = {};
  if (!values.name.trim()) errors.name = 'Name is required';
  if (!values.location.trim()) errors.location = 'Location is required';
  if (!values.eventId.trim()) errors.eventId = 'Event id is required';
  if (!values.startDate) errors.startDate = 'Start date is required';
  if (!values.endDate) errors.endDate = 'End date is required';
  return errors;
};

/**
 * The type is always "VILLA DIOR" (the select was never
 * rendered), the dates ISO strings, the stored or default picture path kept
 * when no file was chosen.
 */
export const formToEvent = (
  values: EventFormValues,
  item: DmsEvent | null,
  hasPictureFile: boolean,
): EventInput => {
  const event: EventInput = {
    name: values.name,
    location: values.location,
    eventId: values.eventId,
    storeCode: values.storeCode,
    type: DMS_EVENT_TYPE.VILLA_DIOR,
    startDate: dateInputToIso(values.startDate),
    endDate: dateInputToIso(values.endDate),
  };
  if (!hasPictureFile) event.picture = item?.picture || DEFAULT_EVENT_PICTURE;
  return event;
};

/** The picture path names its bucket. */
export const eventPictureUrl = (item: DmsEvent | null): string | null => {
  const path = item?.picture;
  if (!path) return null;
  if (path.includes('dms-front-assets'))
    return getImage('dms', undefined, path);
  if (path.includes('abcdior-back')) return getImage('abc', undefined, path);
  return null;
};

export const EVENT_SORT_FIELDS = [
  'name',
  'type',
  'location',
  'storeCode',
  'eventId',
  'startDate',
  'endDate',
] as const;
export type EventSortField = (typeof EVENT_SORT_FIELDS)[number];
export type EventSort = ListSort<EventSortField>;

/** Dates as timestamps (0 when missing), text lower-cased. */
export const sortedEvents = (
  items: readonly DmsEvent[],
  sort: EventSort,
): DmsEvent[] =>
  sortByKey(
    items,
    (item) =>
      sort.by === 'startDate' || sort.by === 'endDate'
        ? dateKey(item[sort.by])
        : textKey(item[sort.by]),
    sort.reverse,
  );

/** Timestamp of an ISO date, 0 when missing. */
const dateKey = (value: string | null | undefined): number =>
  value ? new Date(value).getTime() : 0;

/** `DD/MM/YYYY`, '-' when missing. */
export const formatEventDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};
